import { getContext, put, select, take, takeLatest } from 'redux-saga/effects';
import { TrackJS } from 'trackjs';
import { RootAction } from 'store/state';
import { INITIATE_PAYMENT } from 'store/state/app/types';
import { initiatePayment } from 'store/state/app/actions';
import { mutate } from 'store/state/mutationsResponse/actions';
import { SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import { initIframePaymentResponseSelector } from 'store/state/mutationsResponse/selectors';
import { userProfileSelector } from 'store/state/domainData/selectors';
import { LoadType, MutationType } from './apiService/types';
import { getActivePromotionPlansSelector } from 'store/state/domainData/selectors/common';
import { Domain } from 'store/state/domainData';
import { find } from 'lodash';
import { privateUserMonetizationEventsSourceSelector } from 'store/state/app/selectors';
import { isBulletinLevelPromotionPlanVariant } from 'utils/privateBulletinPromotionPlan';

// Payment via iFrame. It should get deprecated soon.
function* handleInitiatePayment(action: ReturnType<typeof initiatePayment>) {
  const logger = yield getContext('logger');
  try {
    const { productId, redirectUrl, bulletinsId } = action.payload;

    if (!productId || !redirectUrl) {
      logger.error('Product ID or redirect URL is missing');
      return;
    }

    const currentUser: ReturnType<typeof userProfileSelector> = yield select(userProfileSelector);
    const contactName = currentUser.firstName;
    const phone = currentUser.phone;

    const activePlans: Domain<LoadType.ActivePromotionPlans> = yield select(getActivePromotionPlansSelector);
    const selectedPlan = find(activePlans.data.activePromotionPlans, { id: action.payload.productId });
    const selectedPlanName = selectedPlan.type.toLocaleLowerCase();
    const selectedVariantName = selectedPlan.variant;

    if (isBulletinLevelPromotionPlanVariant(selectedPlan) && !bulletinsId) {
      logger.error(`Bulletins ID is required for this promotion plan variant: ${selectedPlan.variant}`);
      return;
    }

    const bulletinsIds: string[] | undefined = (isBulletinLevelPromotionPlanVariant(selectedPlan) && bulletinsId) ? [ bulletinsId ] : undefined;

    const source = yield select(privateUserMonetizationEventsSourceSelector);

    const paymentEventInfo = `&payment_source=${source}&payment_variant=${selectedVariantName}&payment_promotion_plan=${selectedPlanName}&payment_deal_type=${action.payload.initiateSource}`;

    yield put(mutate({
      mutationType: MutationType.InitIframePayment,
      meta: {
        variables: {
          input: {
            productId,
            redirectUrl: redirectUrl + paymentEventInfo,
            contactName,
            phone,
            bulletinsIds,
          },
        },
      },
    }));

    yield take((a: RootAction) => (a.type === SET_MUTATION_RESPONSE && a.mutationType === MutationType.InitIframePayment));
    const response: ReturnType<typeof initIframePaymentResponseSelector> = yield select(initIframePaymentResponseSelector);

    if ((response.errors && response.errors.length > 0) || !response.data) {
      throw new Error('Mutation initIframePayment failed with: ' + JSON.stringify(response.errors));
    }

    const { iframeUrl } = response.data.initIframePayment;
    window.location.href = iframeUrl;
  }
  catch (e) {
    const errorText = `Initiate Payment saga failed. Error: ${e.message}`;
    logger.error(errorText);
    TrackJS.track(errorText);
  }
}

export function* paymentWatcher() {
  yield takeLatest(INITIATE_PAYMENT, handleInitiatePayment);
}
